import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useObjects } from '../../data/hooks';
import { ContextMenu, ContextMenuItem, cx, Icon, MOTION, Skeleton } from '../../ui';
import { NavIcon } from './NavIcon';
import { navItem, navSectionDisclosure, navSectionGap, navSectionHeading } from './navItem';
import { objectNavGroups } from './objectGroups';
import {
  type GroupFold,
  isGroupOpen,
  useGroupFolds,
  useSidebarHiddenObjects,
  withoutHidden,
} from './sidebarPrefs';

/**
 * The navigation IS the metadata: objects come from the metadata client, so an
 * object created in the admin appears here without a code change. Shared by the
 * desktop sidebar and the mobile drawer.
 *
 * GROUPED SINCE THIS MODULE (D11, D12). It was one flat unsorted run of fourteen
 * entries; it is Clients / Sales / Delivery / Team / System now, each group ranked by
 * where its first member sits. An object nobody has filed renders in a trailing
 * unlabelled section - grouping decides ORDER AND HEADINGS, never whether something
 * appears.
 *
 * THE HEADING BELONGS TO THE LIST (WP6c). It used to be a `<p>` in Sidebar.tsx,
 * next to the list rather than part of it, which meant the sidebar rendered the
 * word "DATA" whether or not there was any data to introduce. On a fresh
 * database - Arif's own, which has no objects in it yet - that is a heading
 * standing over nothing, immediately followed by the next heading. Only the list
 * knows whether it is empty, so only the list can decide not to announce itself.
 * THE SAME RULE NOW HOLDS PER GROUP: a group with no members has no heading,
 * because it does not exist.
 *
 * EMPTY IS NOT AN EMPTY STATE HERE. No heading, no placeholder, no "create your
 * first object" card: the sidebar is chrome, and chrome does not teach - the
 * screen behind it does. The space just stays clean.
 *
 * The mobile drawer passes no `heading`; the sheet's own title says "Data". Its GROUP
 * headings still render, because those are what tell fourteen entries apart (D11).
 *
 * EVERY ROW LEADS WITH A COLOURED TILE (E9), which is the System Settings mechanism and
 * the reason fourteen entries can be scanned rather than read: the eye finds a colour
 * before it finds a word. The tile REPLACES the bare `Icon` at the same inset, so the
 * label's own x-position does not move; an object with no colour draws the graphite
 * tile, so a half-configured instance is still a column of even rows.
 *
 * THE TILE SIZE FOLLOWS THE ROW, not the component: the desktop sidebar's tile is the
 * `nav` size, which is Apple's 16 / 20 / 24 under the person's own "Sidebar icon size",
 * and the phone sheet's is the fixed 28px of its 44px iOS list row. Same list, two
 * pointers, and the row height is `--nav-item-height` doing exactly that switch.
 */
export function ObjectNavList({
  onNavigate,
  heading,
  foldDefault = 'open',
}: {
  readonly onNavigate?: () => void;
  readonly heading?: string | undefined;
  /**
   * Whether a group the person has not folded either way starts open or closed (register A5-4,
   * "closed by default for Ben and the coaches (per role)"). The desktop sidebar passes the
   * role's default (`roleFoldDefault`); the phone's Data screen keeps every group open, as today
   * (coordinator decision Q-NAV-3).
   */
  readonly foldDefault?: GroupFold;
}) {
  const objects = useObjects();
  const { hidden, hide } = useSidebarHiddenObjects();
  const { folds, setFold } = useGroupFolds();
  const location = useLocation();
  /** The group the person closed on the page they are on: it stays closed until they leave. */
  const [closedHere, setClosedHere] = useState<{
    readonly path: string;
    readonly label: string;
  } | null>(null);
  const title = heading === undefined ? null : <p className={navSectionHeading}>{heading}</p>;

  if (objects.isPending) {
    // Placeholders at the row height, so the list does not resize when it loads.
    // The heading shows: a section that is about to have rows is not an empty one.
    return (
      <div aria-busy="true">
        {title}
        <div className="space-y-0.5 px-3">
          <Skeleton className="h-(--nav-item-height)" shape="line" />
          <Skeleton className="h-(--nav-item-height)" shape="line" />
          <Skeleton className="h-(--nav-item-height)" shape="line" />
        </div>
      </div>
    );
  }

  if (objects.isError) {
    return (
      <div>
        {title}
        <p className="px-3 text-xs text-danger">Objects could not be loaded.</p>
      </div>
    );
  }

  if (objects.data.length === 0) {
    return null;
  }

  /**
   * HIDDEN OBJECTS LEAVE THE LIST, AND ONLY THE LIST (fulfillment wave, P2 item 3). The
   * filter runs AFTER grouping, so a group whose members are all hidden has no heading, and
   * nothing else in the product reads the preference: the route, search and links still work.
   */
  const groups = withoutHidden(objectNavGroups(objects.data), hidden);

  return (
    <div>
      {title}
      {groups.map((group) => {
        const label = group.label;
        const current = group.objects.some((object) =>
          isPathInside(location.pathname, `/objects/${object.id}`),
        );
        const open =
          label === null ||
          isGroupOpen({
            stored: folds[label],
            roleDefault: foldDefault,
            current,
            closedHere: closedHere?.label === label && closedHere.path === location.pathname,
          });
        const toggle = (): void => {
          if (label === null) return;
          setFold(label, !open);
          setClosedHere(open && current ? { path: location.pathname, label } : null);
        };
        return (
          <div key={group.label ?? '__ungrouped__'}>
            {group.label !== null && (
              /**
               * THE HEADING IS A DISCLOSURE (fulfillment wave, P2 item 4). hig:
               * https://developer.apple.com/design/human-interface-guidelines/sidebars - "Group
               * hierarchy with disclosure controls if your app has a lot of content"; support:
               * https://support.apple.com/guide/mac-help/customize-the-finder-sidebar-on-mac-mchl83c9e8b8/mac
               * - "Position the pointer over the section heading. To show all items, click the
               * Show button. To hide all items, click the Hide button"; doc:
               * https://developer.apple.com/documentation/appkit/nsoutlineviewdelegate/outlineview(_:isgroupitem:)
               * for the group row itself. A real button carrying `aria-expanded`, so Enter and
               * Space toggle it and Tab reaches it where it reached the rows before. The chevron
               * stays visible rather than appearing on hover as Finder's does, because hover is
               * never the only affordance (DESIGN.md, composition rules). It turns with
               * `MOTION.turn` and the rows appear with the layout, no height animation
               * (`docs/decisions/motion.md`, Disclosure); Reduce Motion collapses the turn.
               */
              <button
                type="button"
                aria-expanded={open}
                onClick={toggle}
                className={cx(
                  navSectionHeading,
                  navSectionGap,
                  navSectionDisclosure,
                  'w-full cursor-default',
                )}
              >
                <span className="flex-1 truncate text-left">{group.label}</span>
                <Icon
                  name="chevron-right"
                  /* The kit's Disclosure box, not the heading's 1em: at 13px the 1.5 hairline
                     scaled to 0.8px and drew the chevron at a third of the heading's ink
                     (round 2, F1). */
                  size="var(--nav-heading-disclosure)"
                  className={cx('shrink-0', MOTION.turn, open && 'rotate-90')}
                />
              </button>
            )}
            {/* THE ITEMS OF ONE GROUP BUTT (package A4). `space-y-0.5` ran this list at a
                42px pitch while the sidebar's own fixed rows - Home, Reports, My tasks,
                Pending changes - ran at the row height, so one column drew two pitches.
                kit: Apple macOS 27 UI Kit.sketch / Sidebars/Light/Examples/Active/Large,
                Items instances at y 20, 60, 100 with a frame height of 40. */}
            {open && (
              <ul>
                {group.objects.map((object) => (
                  /* "Hide from Sidebar" on every OBJECT row and on nothing else (P2 item 3):
                     Home, the Fulfillment rows, Reports, Tasks and Settings are not objects and
                     carry no menu. THE MENU'S TRIGGER IS THE `<li>`, NOT THE LINK: Radix's Slot
                     joins the trigger's `className` with the child's as strings, and NavLink's
                     `className` is a function of its active state, which that join turned into
                     text - every row lost its row construction. */
                  <ContextMenu
                    key={object.id}
                    trigger={
                      <li>
                        <NavLink
                          to={`/objects/${object.id}`}
                          onClick={onNavigate}
                          className={navItem}
                        >
                          {/* The desktop row follows the person's "Sidebar icons" setting; the
                              phone's Data sheet is an iOS list row and keeps its fixed 28px tile
                              (package A3, decision 2). `NavIcon` makes that switch for every list. */}
                          <NavIcon icon={object.icon} color={object.color} />
                          <span className="truncate">{object.labelPlural}</span>
                        </NavLink>
                      </li>
                    }
                  >
                    <ContextMenuItem icon="eye-off" onSelect={() => hide(object)}>
                      Hide from sidebar
                    </ContextMenuItem>
                  </ContextMenu>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** `/objects/<id>` and every path under it (a record of that list) belong to that list. */
function isPathInside(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}
